import React, { useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useLanguage } from '../../contexts/LanguageContext';
import { OperationNotification } from '../../types/operations';
import { groupNotifications } from '../../utils/taskPresentation';
import { ActionButton, IconButton } from './TaskControls';

export default function NotificationGroups({
  notifications,
  stores,
  taskTitles,
  busy,
  onRead,
  onStore,
  onTask,
}: {
  notifications: OperationNotification[];
  stores: { id: string; name: string }[];
  taskTitles: Map<string, string>;
  busy: boolean;
  onRead: (ids: string[]) => void;
  onStore: (id: string) => void;
  onTask: (id: string) => void;
}) {
  const { t } = useLanguage();
  const tr = (key: string) => t('operations.' + key);
  const [expanded, setExpanded] = useState(new Set<string>());
  const [limits, setLimits] = useState<Record<string, number>>({});
  const [limit, setLimit] = useState(10);
  const groups = groupNotifications(notifications);
  return (
    <View style={s.list}>
      {groups.slice(0, limit).map((group) => {
        const open = expanded.has(group.id);
        const title = group.storeId
          ? (stores.find((store) => store.id === group.storeId)?.name ??
            tr('store'))
          : group.taskId
            ? (taskTitles.get(group.taskId) ?? group.items[0].title)
            : tr('taskOtherNotifications');
        const summary = [
          group.missing ? tr('taskMissingDates') + ': ' + group.missing : '',
          group.expired ? tr('overdue') + ': ' + group.expired : '',
          group.upcoming ? tr('taskUpcomingDates') + ': ' + group.upcoming : '',
        ]
          .filter(Boolean)
          .join(' · ');
        return (
          <View key={group.id} style={s.group}>
            <View style={s.header}>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={title}
                accessibilityState={{ expanded: open }}
                aria-expanded={open}
                style={s.disclosure}
                onPress={() =>
                  setExpanded((previous) => {
                    const next = new Set(previous);
                    if (next.has(group.id)) next.delete(group.id);
                    else next.add(group.id);
                    return next;
                  })
                }
              >
                <View style={s.icon}>
                  <Ionicons
                    name={group.storeId ? 'cube-outline' : 'clipboard-outline'}
                    size={19}
                    color="#475569"
                  />
                </View>
                <View style={s.flex}>
                  <Text style={s.title}>{title}</Text>
                  <Text style={s.meta}>
                    {summary ||
                      tr('taskNotificationCount') + ': ' + group.items.length}
                  </Text>
                </View>
                {!!group.unread && (
                  <View style={s.badge}>
                    <Text style={s.badgeText}>{group.unread}</Text>
                  </View>
                )}
                <Ionicons
                  name={open ? 'chevron-up' : 'chevron-down'}
                  size={17}
                  color="#64748B"
                />
              </Pressable>
            </View>
            <View style={s.actions}>
              {group.storeId ? (
                <ActionButton
                  title={tr('taskOpenStore')}
                  icon="arrow-forward"
                  tone="quiet"
                  disabled={busy}
                  onPress={() => onStore(group.storeId!)}
                />
              ) : (
                group.taskId && (
                  <ActionButton
                    title={tr('taskOpenTask')}
                    icon="arrow-forward"
                    tone="quiet"
                    disabled={busy}
                    onPress={() => onTask(group.taskId!)}
                  />
                )
              )}
              {!!group.unread && (
                <IconButton
                  label={tr('taskMarkGroupRead') + ': ' + title}
                  icon="checkmark-done-outline"
                  disabled={busy}
                  onPress={() =>
                    onRead(
                      group.items
                        .filter((item) => !item.read)
                        .map((item) => item.id),
                    )
                  }
                />
              )}
            </View>
            {open && (
              <View style={s.details}>
                {group.items.slice(0, limits[group.id] ?? 10).map((item) => (
                  <View key={item.id} style={s.item}>
                    <View style={[s.dot, item.read && s.dotRead]} />
                    <View style={s.flex}>
                      <Text style={s.itemTitle}>{item.title}</Text>
                      <Text style={s.body}>{item.body}</Text>
                    </View>
                    {!item.read && (
                      <IconButton
                        label={tr('markRead') + ': ' + item.title}
                        icon="checkmark-outline"
                        disabled={busy}
                        onPress={() => onRead([item.id])}
                      />
                    )}
                  </View>
                ))}
                {group.items.length > (limits[group.id] ?? 10) && (
                  <ActionButton
                    title={tr('taskShowMore')}
                    tone="quiet"
                    onPress={() =>
                      setLimits((previous) => ({
                        ...previous,
                        [group.id]: (previous[group.id] ?? 10) + 10,
                      }))
                    }
                  />
                )}
              </View>
            )}
          </View>
        );
      })}
      {groups.length > limit && (
        <ActionButton
          title={tr('taskShowMore')}
          onPress={() => setLimit(limit + 10)}
        />
      )}
    </View>
  );
}
const s = StyleSheet.create({
  list: {
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 10,
    overflow: 'hidden',
    backgroundColor: '#FFFFFF',
  },
  group: { borderBottomWidth: 1, borderColor: '#E2E8F0' },
  header: { paddingHorizontal: 12 },
  disclosure: {
    flex: 1,
    flexDirection: 'row',
    gap: 10,
    alignItems: 'center',
    minHeight: 64,
    paddingVertical: 10,
  },
  icon: {
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    backgroundColor: '#F1F5F9',
  },
  flex: { flex: 1, minWidth: 0 },
  title: { color: '#0F172A', fontSize: 14, fontWeight: '600' },
  meta: { color: '#64748B', fontSize: 12, marginTop: 4, lineHeight: 17 },
  badge: {
    minWidth: 24,
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderRadius: 12,
    backgroundColor: '#DBEAFE',
  },
  badgeText: {
    color: '#1D4ED8',
    fontSize: 12,
    fontWeight: '600',
    textAlign: 'center',
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 8,
    gap: 0,
    paddingBottom: 4,
  },
  details: { borderTopWidth: 1, borderColor: '#F1F5F9', marginHorizontal: 12 },
  item: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderColor: '#F1F5F9',
  },
  dot: { width: 6, height: 6, borderRadius: 3, backgroundColor: '#2563EB' },
  dotRead: { backgroundColor: '#CBD5E1' },
  itemTitle: { fontSize: 12, color: '#334155', fontWeight: '600' },
  body: { color: '#64748B', fontSize: 12, lineHeight: 17, marginTop: 2 },
});
